import { Button, Badge, UncontrolledTooltip } from "reactstrap";
import { FiFilter, FiRefreshCw, FiExternalLink } from "react-icons/fi";
import { useState } from "react";

export const LogDataColumns = (viewLogDetails: any, filterLogs: any) => [
  {
    name: "Event Type",
    selector: (row: any) => row.event_type,
    sortable: true,
    cell: (row: any) => {
      let badgeClass = "";

      switch (row.event_type) {
        case "request":
          badgeClass = "bg-info";
          break;
        case "response":
          badgeClass = "bg-success";
          break;
        case "exception":
          badgeClass = "bg-danger";
          break;
        default:
          badgeClass = "bg-secondary";
          break;
      }

      return (
        <div>
          <span
            className={`badge ${badgeClass}`}
            style={{ padding: "0.4em 0.6em", fontSize: "0.85rem" }}
          >
            {row.event_type?.charAt(0).toUpperCase() + row.event_type?.slice(1)}
          </span>
        </div>
      );
    }
  },
  {
    name: "Method",
    selector: (row: any) => row.method,
    sortable: true,
    cell: (row: any) => {
      let methodColor = "";

      switch (row.method?.toUpperCase()) {
        case "GET":
          methodColor = "bg-success";
          break;
        case "POST":
          methodColor = "bg-primary";
          break;
        case "PUT":
          methodColor = "bg-info";
          break;
        case "DELETE":
          methodColor = "bg-danger";
          break;
        case "PATCH":
          methodColor = "bg-warning text-dark";
          break;
        default:
          methodColor = "bg-secondary";
      }

      return (
        <div>
          <span
            className={`badge ${methodColor}`}
            style={{ padding: "0.4em 0.6em", fontSize: "0.85rem" }}
          >
            {row.method?.toUpperCase() || "N/A"}
          </span>
        </div>
      );
    }
  },
  {
    name: "URL",
    selector: (row: any) => row.url,
    sortable: true,
    cell: (row: any) => (
      <div style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {row.url || row.full_path || row.path || "N/A"}
      </div>
    ),
  },
  {
    name: "Status Code",
    selector: (row: any) => row.status_code,
    sortable: true,
    cell: (row: any) => {
      let statusClass = "";

      // Status code color based on HTTP status ranges
      if (!row.status_code) {
        statusClass = "bg-secondary";
      } else if (row.status_code < 300) {
        statusClass = "bg-success";
      } else if (row.status_code < 400) {
        statusClass = "bg-info";
      } else if (row.status_code < 500) {
        statusClass = "bg-warning text-dark";
      } else {
        statusClass = "bg-danger";
      }

      return (
        <div>
          <span
            className={`badge ${statusClass}`}
            style={{ padding: "0.4em 0.6em", fontSize: "0.85rem" }}
          >
            {row.status_code || "N/A"}
          </span>
        </div>
      );
    }
  },
  {
    name: "Timestamp",
    selector: (row: any) => row.timestamp,
    sortable: true,
    cell: (row: any) => (
      <div>
        <span className="badge bg-light text-dark">
          {row.timestamp || "N/A"}
        </span>
      </div>
    ),
  },
  {
    name: "Client IP",
    selector: (row: any) => row.client_ip,
    sortable: true,
    cell: (row: any) => (
      <div style={{ fontSize: "0.9rem" }}>
        {row.client_ip || "Unknown"}
      </div>
    ),
  },
  {
    name: "User",
    selector: (row: any) => row?.user || '-',
    sortable: true,
    cell: (row: any) => (
      <div style={{ fontSize: "0.9rem" }}>
        {row?.user || "Unknown"}
      </div>
    ),
  },
  {
    name: "Duration",
    selector: (row: any) => row.duration,
    sortable: true,
    cell: (row: any) => {
      // Only show for response events
      if (row.event_type !== "response" || !row.duration) {
        return <span className="text-muted">-</span>;
      }

      let durationClass = "";
      if (row.duration < 0.1) {
        durationClass = "bg-success";
      } else if (row.duration < 0.5) {
        durationClass = "bg-info";
      } else if (row.duration < 1.0) {
        durationClass = "bg-warning text-dark";
      } else {
        durationClass = "bg-danger";
      }

      return (
        <div>
          <span
            className={`badge ${durationClass}`}
            style={{ padding: "0.4em 0.6em", fontSize: "0.85rem" }}
          >
            {row.duration.toFixed(3)}s
          </span>
        </div>
      );
    }
  },
  {
    name: "Actions",
    cell: (row: any) => {
      const viewId = `view-${row._id}`;

      return (
        <div className="d-flex gap-2">
          <span id={viewId} style={{ cursor: "pointer" }} onClick={() => viewLogDetails(row._id)}>
            <FiExternalLink size={18} color="#0d6efd" />
          </span>
          <UncontrolledTooltip target={viewId} placement="top">
            View Details
          </UncontrolledTooltip>
        </div>
      );
    }
  }

];

// Usage component
const LogsTable = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    eventType: "",
    statusCode: "",
    method: ""
  });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/logs');
      const data = await response.json();
      setLogs(data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const viewLogDetails = (log: any) => {
    console.log("Viewing log details:", log);
    // Implement modal or navigate to details page
  };

  const filterLogs = () => {
    console.log("Filtering logs with:", filterOptions);
    // Implement filters
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-3">
        <div className="col">
          <h2>System Logs</h2>
        </div>
        <div className="col-auto">
          <Button color="primary" className="me-2" onClick={fetchLogs}>
            <FiRefreshCw size={16} className="me-1" /> Refresh
          </Button>
          <Button color="secondary" onClick={() => { }}>
            <FiFilter size={16} className="me-1" /> Filter
          </Button>
        </div>
      </div>

      {/* Implement DataTable component with LogDataColumns */}
    </div>
  );
};

export default LogsTable;